// SurveyTaking.jsx
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { Save, CheckCircle, AlertCircle, Info, ArrowLeft, ShieldCheck } from 'lucide-react';
import surveyService from '../../services/surveyService';
import { ERROR_CODES, mapBackendError, createErrorObject } from '../../utils/errorCodes';
import { evaluateNavigationRules } from '../../utils/navigationEvaluator';
import {
  collectScreenQuestionIds,
  buildSkipRecordsForBypass,
  mergeSkipRecords,
  skippedQuestionIdSet,
  validateRequiredAtSubmit,
} from '../../utils/skipValidation';
import ErrorPopup from '../Shared/ErrorPopup';
import ScreenRenderer from './ScreenRenderer';
import { DEVICE_DIMENSIONS } from '../AssessmentBuilder/ScreenBuilder/constants';
import { isLocalDevLayoutActive } from '../../utils/localDevLayout';
import './Styles/SurveyTaking.css';
import { useRef } from 'react';

const QuestionTypes = {
  TEXT: 'text',
  MULTIPLE_CHOICE: 'multiple_choice',
  CHECKBOX: 'checkbox',
  RATING: 'rating',
  LIKERT: 'likert',
  DROPDOWN: 'dropdown',
  YES_NO: 'yes_no',
  NUMERIC: 'numeric',
  DATE: 'date',
  EMAIL: 'email',
  NOMINAL: 'nominal',
  BOOLEAN: 'boolean'
};

// FECOA-1468 — a minimal, premium completion ring used in the assessment chrome.
// Pure presentation: a hairline track with a teal progress arc that animates as the
// participant advances. Kept crisp (rendered as app chrome, never inside the scaled
// canvas) so it stays sharp at every device scale.
const ProgressRing = ({ value = 0, size = 30, stroke = 2.5 }) => {
  const clamped = Math.max(0, Math.min(Number(value) || 0, 100));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (clamped / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden="true">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        className="stroke-gray-200"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        className="stroke-primary-600 transition-[stroke-dashoffset] duration-700 ease-out"
      />
    </svg>
  );
};

const SurveyTaking = ({ surveyApi, homePath = '/participant-dashboard' } = {}) => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  // FECOA-1320: the survey-taking flow is respondent-agnostic. Participants use
  // the default `surveyService`; caregivers (ObsRO) inject a caregiver adapter
  // that points the load/save/submit calls at the caregiver endpoints. Default
  // preserves the participant behavior exactly.
  const svc = surveyApi || surveyService;
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [errorObject, setErrorObject] = useState(null);
  const [success, setSuccess] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState(0);
  const [surveyName, setSurveyName] = useState('');
  const [surveyDescription, setSurveyDescription] = useState('');
  const [goingBack, setGoingBack] = useState(false);
  const [inSequentialMode, setInSequentialMode] = useState(false);
  const [isLastQuestion, setIsLastQuestion] = useState(false);
  const [isFirstQuestion, setIsFirstQuestion] = useState(false);
  const [isCheckingLinkedSurveys, setIsCheckingLinkedSurveys] = useState(false);
  const [statusList, setStatusList] = useState('[]');

  // Assessment Builder state
  const [isAssessmentMode, setIsAssessmentMode] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(null);
  const [screenResponses, setScreenResponses] = useState({});
  const [screenValidationErrors, setScreenValidationErrors] = useState({});
  const [screenHistory, setScreenHistory] = useState([]);  // For back navigation
  const [navigationAlert, setNavigationAlert] = useState(null);  // For skip logic alerts

  // Fast-nav mode: all screens loaded upfront, navigation is instant
  const [allScreens, setAllScreens] = useState([]);
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [isAdaptiveAssessment, setIsAdaptiveAssessment] = useState(false);
  const pendingSavePromises = useRef([]);  // Track async saves for flush-on-submit
  const responseMapRef = useRef({});  // { screen_id: { questionId: answer } } — preserves answers across back/forward nav
  const screenStartTimeRef = useRef(Date.now());  // Timestamp when current screen first became visible (for time-on-screen metadata)

  // Track every questionId the participant has actually seen (screen rendered).
  // Used at submit time to distinguish "unanswered required" (must block) from
  // "never-reached required" (legitimate — skipped by logic OR unvisited).
  const [visitedQuestionIds, setVisitedQuestionIds] = useState(() => new Set());
  // Records of questions bypassed by skip-logic: [{question_id, skipped_by_rule_id}]
  // Sent in the submit payload as `skipped_questions`.
  const [skippedQuestions, setSkippedQuestions] = useState([]);
  // FECOA-1396: track rule_ids that already fired this session so the
  // cross-screen evaluator doesn't re-trigger a skip on every subsequent
  // submit. Mirrors the backend `fired_rule_ids` contract.
  const [firedRuleIds, setFiredRuleIds] = useState(() => new Set());

  // Assessment settings from builder_metadata (controls UI features)
  const [assessmentSettings, setAssessmentSettings] = useState({
    show_progress_bar: true,
    allow_back_navigation: true,
    validate_responses: true,
    allow_skip_questions: false
  });

  // FECOA-1468 — gentle screen-to-screen cross-fade for the live canvas. This is
  // opacity-only on a wrapper that never changes size, so ScreenRenderer's scaling
  // measurement (and the design layout) is completely unaffected. useLayoutEffect
  // sets opacity to 0 before paint, then fades in, so there is no flash of the new
  // screen — and ScreenRenderer is never remounted, so no scale "pop".
  const canvasFadeControls = useAnimationControls();
  useLayoutEffect(() => {
    if (!isAssessmentMode) return;
    canvasFadeControls.set({ opacity: 0 });
    canvasFadeControls.start({
      opacity: 1,
      transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] },
    });
  }, [currentScreen?.screen_id, currentScreenIndex, isAssessmentMode, canvasFadeControls]);

  // Check if current question is the last question
  // We'll keep this function but primarily rely on the backend flag
  const checkIsLastQuestion = () => {
    return totalQuestions > 0 && answeredQuestions + 1 >= totalQuestions;
  };

  const effectRan = useRef(false);

  // Helper function to handle and display errors with error codes
  const handleError = (error, fallbackErrorCode = ERROR_CODES.SURVEY.LOAD_FAILED) => {
    console.error('Error occurred:', error);
    
    let errorObj;
    if (error.response?.data?.error) {
      // Backend error with message
      errorObj = mapBackendError(error.response.data.error);
    } else if (error.message) {
      // Standard error with message
      errorObj = mapBackendError(error.message);
    } else {
      // Fallback error
      errorObj = createErrorObject(fallbackErrorCode);
    }
    
    setErrorObject(errorObj);
    setError(errorObj.userMessage);
  };

  // Clear error
  const clearError = () => {
    setError(null);
    setErrorObject(null);
  };

  // Retry handler
  const handleRetry = () => {
    clearError();
    fetchNextQuestion();
  };

  // ─── Fast-nav: upfront load ───────────────────────────────────────────────

  const loadAllScreensAndStart = async () => {
    try {
      setLoading(true);
      clearError();
      const data = await svc.loadAllScreens(assignmentId);

      if (!data.screens || data.screens.length === 0) {
        setSuccess(true);
        return;
      }

      setAllScreens(data.screens);
      setIsAdaptiveAssessment(false);
      setSurveyName(data.survey_name || '');
      setSurveyDescription(data.survey_description || '');

      // Seed session response map with any pre-existing (resume) responses
      responseMapRef.current = {};
      data.screens.forEach(s => {
        if (s.existing_responses && Object.keys(s.existing_responses).length > 0) {
          responseMapRef.current[s.screen_id] = { ...s.existing_responses };
        }
      });

      // Start at the first unanswered screen (resume support)
      const resumeIndex = data.screens.findIndex(s =>
        !s.existing_responses || Object.keys(s.existing_responses).length === 0
      );
      const startIndex = resumeIndex >= 0 ? resumeIndex : 0;

      const firstScreen = data.screens[startIndex];
      setCurrentScreenIndex(startIndex);
      setCurrentScreen(firstScreen);
      setScreenResponses(responseMapRef.current[firstScreen.screen_id] || firstScreen.existing_responses || {});
      screenStartTimeRef.current = Date.now();

      // Seed history with any already-answered screens before startIndex so Back works on resume
      if (startIndex > 0) {
        setScreenHistory(data.screens.slice(0, startIndex).map(s => ({
          ...s,
          existing_responses: responseMapRef.current[s.screen_id] || s.existing_responses || {}
        })));
      }
      setIsAssessmentMode(true);
      setIsFirstQuestion(startIndex === 0);
      setIsLastQuestion(startIndex === data.screens.length - 1);
      setTotalQuestions(data.total_screens);
      setAnsweredQuestions(startIndex);
      setProgress(data.screens.length > 0 ? Math.round((startIndex / data.screens.length) * 100) : 0);

      if (data.assessment_settings) {
        setAssessmentSettings(prev => ({ ...prev, ...data.assessment_settings }));
      }
    } catch (err) {
      // Fall back to legacy question-by-question mode (e.g. survey has no screens)
      const status = err.response?.status;
      if (status === 404 || status === 400) {
        await fetchNextQuestion(null);
      } else {
        handleError(err, ERROR_CODES.SURVEY.LOAD_FAILED);
      }
    } finally {
      setLoading(false);
    }
  };

  // Flush all in-flight async saves — called before submit to ensure nothing is lost
  const flushPendingSaves = async () => {
    const promises = pendingSavePromises.current.splice(0);
    if (promises.length > 0) {
      await Promise.allSettled(promises);
    }
  };

  const evaluateNavRulesClientSide = (responses, screenIdx) => {
    const screen = allScreens[screenIdx];
    console.log('[navRules] call', {
      screenIdx,
      screen_id: screen?.screen_id,
      hasRules: !!screen?.navigationRules && Object.keys(screen.navigationRules).length > 0,
      ruleKeys: screen?.navigationRules ? Object.keys(screen.navigationRules) : [],
      responses,
      historyLen: screenHistory.length,
      firedRuleIds: [...firedRuleIds],
    });
    // FECOA-1396: pass the full screen list so rules attached to OTHER
    // screens still get a chance to fire — matches the backend
    // `evaluate_navigation_across_survey` contract. Without this, a
    // cumulative / combination rule whose trigger answer lives on a
    // different screen than the rule's attach point would be invisible.
    const result = evaluateNavigationRules({
      screen,
      responses,
      screenHistory,
      allScreens,
      firedRuleIds: [...firedRuleIds],
      debug: true,
    });
    console.log('[navRules] result', result);
    if (result?.rule_id) {
      setFiredRuleIds((prev) => {
        if (prev.has(String(result.rule_id))) return prev;
        const next = new Set(prev);
        next.add(String(result.rule_id));
        return next;
      });
    }
    return result;
  };

  // ─── Track visited questions (for skip-aware required validation) ────────

  // Whenever a screen renders, mark every question on it as visited. The set
  // is additive — re-visits don't un-visit, and skip rules never remove
  // questions from the visited set (a re-visited question that gets skipped
  // over on the second pass remains visited but will also appear in
  // skippedQuestions, and skipped takes precedence at submit time).
  useEffect(() => {
    if (!currentScreen) return;
    const ids = collectScreenQuestionIds(currentScreen);
    if (ids.length === 0) return;
    setVisitedQuestionIds(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const id of ids) {
        const key = String(id);
        if (!next.has(key)) {
          next.add(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [currentScreen]);

  // ─── Validity polling ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!isAssessmentMode || !allScreens.length) return;
    const interval = setInterval(async () => {
      try {
        await svc.checkSessionValid(assignmentId);
      } catch (err) {
        const code = err.response?.data?.error_code;
        if (code === 'PARTICIPANT_DEACTIVATED' || code === 'SURVEY_DEACTIVATED') {
          handleError(err, ERROR_CODES.SURVEY.LOAD_FAILED);
        }
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [isAssessmentMode, allScreens.length, assignmentId]);

  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!effectRan.current) {
      effectRan.current = true;
      loadAllScreensAndStart();
    }
  }, [assignmentId]);

  useEffect(() => {
    if (isLastQuestion) {
      console.log("Last question detected, updating UI...");
      // Force a re-render of the button by adding a class
      setTimeout(() => {
        const isLastQuestionBtn = document.querySelector('.question-next-button');
        if (isLastQuestionBtn) {
          isLastQuestionBtn.classList.add('is-last-question');
          isLastQuestionBtn.setAttribute('data-last-question', 'true');
        }
      }, 100);
    }
  }, [isLastQuestion]);

  useEffect(() => {
    // Legacy question-by-question flow relies on this to check for linked surveys on completion.
    // Assessment mode handles linked-survey routing inline in handleScreenNavigation's submit path,
    // so skip here to avoid a duplicate next_linked_assignment call.
    if (success && !currentQuestion && !isAssessmentMode) {
      checkForLinkedSurveys();
    }
  }, [success, currentQuestion, isAssessmentMode]);

  const updateProgress = async () => {
    try {
      const progressData = await svc.getSurveyProgress(assignmentId);
      const { total_questions, answered_questions } = progressData;
      setTotalQuestions(total_questions);
      setAnsweredQuestions(answered_questions);
      const progressPercent = total_questions > 0 
        ? Math.round((answered_questions / total_questions) * 100)
        : 0;
      setProgress(progressPercent);
    } catch (err) {
      console.error('Error fetching progress:', err);
    }
  };

  const fetchNextQuestion = async (status_list, currentScreenId = null, targetScreenId = null, prefetchedData = null) => {
    try {
      setLoading(true);
      clearError();
      // Clear the previous response to prevent it from bleeding into the next question
      setResponse(null);
      setScreenResponses({});
      setScreenValidationErrors({});

      if (status_list == null)
        status_list = statusList;

      // Use prefetched data if available (fired in parallel with saves), otherwise fetch now
      const data = prefetchedData ?? await svc.getNextQuestion(assignmentId, status_list, currentScreenId, targetScreenId);
      
      console.log("getNextQuestion response:", data);
      
      if (data.error) {
        throw new Error(data.error);
      }
      setStatusList(data.status_list)
      
      if (!data || data.message === 'All questions have been answered') {
        setCurrentQuestion(null);
        setCurrentScreen(null);
        setIsAssessmentMode(false);
        setSuccess(true);
      } else if (data.screen_id) {
        // SCREEN-BASED MODE: Survey uses Assessment Builder screens with layout
        console.log("Screen-based survey - loading screen data:", data);
        setIsAssessmentMode(true);
        setIsAdaptiveAssessment(data.adaptive_survey === true);
        setCurrentScreen(data);
        setCurrentQuestion(null);
        if (data.adaptive_survey === true) {
          setAllScreens([]);
          setCurrentScreenIndex(0);
          setScreenHistory([]);
        }
        
        if (data.survey_name) {
          setSurveyName(data.survey_name);
        }
        if (data.survey_description) {
          setSurveyDescription(data.survey_description || '');
        }
        setSuccess(false);
        
        // Initialize responses with existing responses (for resuming assessment)
        if (data.existing_responses && Object.keys(data.existing_responses).length > 0) {
          console.log("Loading existing responses:", data.existing_responses);
          setScreenResponses(data.existing_responses);
        } else {
          // Clear responses for new screen
          setScreenResponses({});
        }
        
        // Set navigation flags (reuse question flags for screen navigation)
        setIsFirstQuestion(data.is_first_screen !== undefined ? data.is_first_screen : false);
        setIsLastQuestion(data.is_last_screen !== undefined ? data.is_last_screen : false);
        
        // Load assessment settings from backend (controls progress bar, back nav, etc.)
        if (data.assessment_settings) {
          console.log("Loading assessment settings:", data.assessment_settings);
          setAssessmentSettings(prev => ({ ...prev, ...data.assessment_settings }));
        }
        
      } else if (data.id && data.text) {
        // LEGACY MODE: Question-by-question response
        console.log("Legacy mode detected - question data:", data);
        setIsAssessmentMode(false);
        setIsAdaptiveAssessment(false);
        setCurrentQuestion(data);
        setCurrentScreen(null);
        
        if (data.survey) {
          setSurveyName(data.survey.name || '');
          setSurveyDescription(data.survey.short_description || '');
        }
        setSuccess(false);
        
        // Check if this is the last question based on backend response
        if (data.is_last_question !== undefined) {
          console.log("Setting isLastQuestion to:", data.is_last_question);
          setIsLastQuestion(data.is_last_question);
        }
        
        // Check if this is the first question based on the backend flag
        if (data.is_first_question !== undefined) {
          console.log("Setting isFirstQuestion to:", data.is_first_question);
          setIsFirstQuestion(data.is_first_question);
        }
      } else {
        throw new Error('Invalid question data received');
      }
    } catch (err) {
      handleError(err, ERROR_CODES.SURVEY.LOAD_FAILED);
      setCurrentQuestion(null);
      setCurrentScreen(null);
    } finally {
      setLoading(false);
      // Progress is updated from save_response data, no separate call needed
    }
  };

  const fetchPreviousQuestion = async () => {
    if (!currentQuestion) return;
    
    try {
      setLoading(true);
      clearError();
      setGoingBack(true);
      
      const data = await svc.getPreviousQuestion(assignmentId, currentQuestion.id, statusList);
      setStatusList(data.status_list)
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      if (data.message === 'This is the first question in the survey') {
        // We're already at the first question
        setGoingBack(false);
        setIsFirstQuestion(true);
      } else if (data.id && data.text) {
        // Don't reset the response when going to the previous question
        setCurrentQuestion(data);
        if (data.survey) {
          setSurveyName(data.survey.name || '');
          setSurveyDescription(data.survey.short_description || '');
        }
        setSuccess(false);
        
        // Try to retrieve previous response for this question
        try {
          // Reset the response first to prevent previous question's response from bleeding through
          setResponse(null);
          
          const previousResponse = await svc.getQuestionResponse(assignmentId, data.id);
          if (previousResponse && previousResponse.response_data && previousResponse.response_data.answer) {
            setResponse(previousResponse.response_data.answer);
          } else {
            // Explicitly set to null if no previous response found
            setResponse(null);
          }
        } catch (responseErr) {
          console.error("Error fetching previous response:", responseErr);
          // Don't show an error to the user for this since it's not critical
          setResponse(null);
        }
        
        // Check if this is the first question based on the backend flag
        if (data.is_first_question !== undefined) {
          console.log("Setting isFirstQuestion to:", data.is_first_question);
          setIsFirstQuestion(data.is_first_question);
        }
        
        // Check if this is the last question based on backend flag
        if (data.is_last_question !== undefined) {
          console.log("Setting isLastQuestion to:", data.is_last_question);
          setIsLastQuestion(data.is_last_question);
        } else {
          // If we're navigating to a previous question and the backend doesn't specify
          // is_last_question, assume it's NOT the last question
          console.log("Resetting isLastQuestion to false for previous question");
          setIsLastQuestion(false);
        }
        
        // Don't forget to reset the goingBack state
        setGoingBack(false);
        
        // Set sequential mode when navigating back: jdt! only for non-adaptive surveys
        if (data.status_list == null || data.status_list.length < 10)
          setInSequentialMode(true);
      } else {
        throw new Error('Invalid question data received');
      }
    } catch (err) {
      handleError(err, ERROR_CODES.SURVEY.PREVIOUS_QUESTION_ERROR);
      setGoingBack(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveResponse = async () => {
    if (!currentQuestion || !response) return;

    try {
      setSaving(true);
      clearError();

      const data = await svc.saveResponse(
        assignmentId,
        currentQuestion.id,
        { answer: response },
        statusList
      );

      setStatusList(data.status_list)

      console.log("Response from save_response:", data);
      
      if (data.error) {
        throw new Error(data.error);
      }

      // Check if this is the last question based on backend response
      if (data.is_last_question !== undefined) {
        console.log("Setting isLastQuestion to:", data.is_last_question);
        setIsLastQuestion(data.is_last_question);
        
        // If this is the last question and we just submitted it, show the completion screen
        if (data.is_last_question) {
          console.log("Last question answered, survey is completed");
          // Progress already updated from save_response data
          
          // Mark this survey as completed
          try {
            await svc.updateAssignmentStatus(assignmentId, 'completed');
          } catch (statusErr) {
            console.error("Error updating assignment status:", statusErr);
            // Don't block the user experience if this fails
          }
          
          // Check for linked surveys first before showing completion screen
          setIsCheckingLinkedSurveys(true);
          try {
            console.log("Checking for next linked survey assignment...");
            const nextLinkedAssignment = await svc.getNextLinkedAssignment(assignmentId);
            console.log("Next linked assignment response:", nextLinkedAssignment);
            
            if (nextLinkedAssignment && nextLinkedAssignment.id) {
              console.log("Found next linked survey, navigating to:", nextLinkedAssignment.id);
              // Navigate to the next linked survey instead of showing completion screen
              // Force a full page reload to ensure the component remounts
              window.location.href = `/survey-taking/${nextLinkedAssignment.id}`;
              return; // Exit early to prevent setting success state
            }
          } catch (err) {
            console.error("Error checking for linked surveys:", err);
          } finally {
            setIsCheckingLinkedSurveys(false);
          }
          
          // Only trigger the completion screen if no linked surveys or error occurred
          setCurrentQuestion(null);
          setSuccess(true);
          
          return; // Exit early so we don't continue to the next question
        }
      } else {
        console.log("is_last_question flag not found in response");
      }
      
      // Check if this is the first question based on backend response
      if (data.is_first_question !== undefined) {
        console.log("Setting isFirstQuestion to:", data.is_first_question);
        setIsFirstQuestion(data.is_first_question);
      } else {
        console.log("is_first_question flag not found in response");
      }
      
      // Update progress from save_response data (no separate API call needed)
      if (data.total_questions !== undefined && data.answered_questions !== undefined) {
        setTotalQuestions(data.total_questions);
        setAnsweredQuestions(data.answered_questions);
        const progressPercent = data.total_questions > 0 
          ? Math.round((data.answered_questions / data.total_questions) * 100)
          : 0;
        setProgress(progressPercent);
      }
      
      setSaving(false);
      
      if (inSequentialMode) {
        // In sequential mode, get the next question in sequence
        try {
          const data = await svc.getNextSequentialQuestion(assignmentId, currentQuestion.id);
          
          // First, clear the previous response to prevent bleeding into the next question
          setResponse(null);
          
          if (data.message === 'This is the last question in the survey') {
            // If we've reached the end of the survey
            setCurrentQuestion(null);
            setSuccess(true);
            setInSequentialMode(false); // Reset to normal mode
          } else if (data.id && data.text) {
            setCurrentQuestion(data);
            if (data.survey) {
              setSurveyName(data.survey.name || '');
              setSurveyDescription(data.survey.short_description || '');
            }
            setSuccess(false);
            
            // Optionally retrieve previous response if available
            try {
              const previousResponse = await svc.getQuestionResponse(assignmentId, data.id);
              if (previousResponse && previousResponse.response_data && previousResponse.response_data.answer) {
                setResponse(previousResponse.response_data.answer);
              } else {
                // Explicitly set to null if no previous response found
                setResponse(null);
              }
            } catch (responseErr) {
              console.error("Error fetching previous response:", responseErr);
              // Don't show an error to the user for this since it's not critical
              setResponse(null);
            }
            
            // Check if this is the last question based on backend response
            if (data.is_last_question !== undefined) {
              console.log("Setting isLastQuestion to:", data.is_last_question);
              setIsLastQuestion(data.is_last_question);
            }
            
            // Check if this is the first question based on the backend flag
            if (data.is_first_question !== undefined) {
              console.log("Setting isFirstQuestion to:", data.is_first_question);
              setIsFirstQuestion(data.is_first_question);
            }
          } else if (data.error) {
            // Handle case when we get an error response from the API
            console.error("Error in sequential navigation:", data.error);
            
            // Fall back to normal navigation mode
            setInSequentialMode(false);
            fetchNextQuestion(data.status_list);
          } else {
            // If we get an invalid response, fall back to normal mode
            console.warn("Invalid response from getNextSequentialQuestion - falling back to normal mode");
            setInSequentialMode(false);
            fetchNextQuestion(data.status_list);
          }
        } catch (err) {
          console.error('Error fetching next sequential question:', err);
          // If there's an error, fall back to normal mode
          setInSequentialMode(false);
          fetchNextQuestion(data.status_list);
        }
      } else {
        // Normal mode - get the next unanswered question
        fetchNextQuestion(data.status_list);
      }
    } catch (err) {
      handleError(err, ERROR_CODES.SURVEY.SAVE_FAILED);
    } finally {
      setSaving(false);
    }
  };

  const handleResponseChange = (value) => {
    setResponse(value);
  };

  const renderQuestionInput = () => {
    if (!currentQuestion) return null;

    const {
      type,
      text,
      options,
      required,
      min_value,
      max_value,
      step_value,
      placeholder_text,
      help_text
    } = currentQuestion;

    const commonProps = {
      required,
      'aria-label': text,
      ...(help_text && { 'aria-describedby': `help-${currentQuestion.id}` })
    };

    switch (type) {
      case QuestionTypes.TEXT:
        return (
          <textarea
            {...commonProps}
            value={response === null ? '' : response}
            onChange={(e) => {
              handleResponseChange(e.target.value);
              // Auto-expand: reset height then set to scrollHeight, capped at 300px
              e.target.style.height = 'auto';
              const newHeight = Math.min(Math.max(e.target.scrollHeight, 80), 300);
              e.target.style.height = `${newHeight}px`;
              e.target.style.overflowY = e.target.scrollHeight > 300 ? 'auto' : 'hidden';
            }}
            className="w-full p-4 bg-[#F1F3F6] rounded-lg border border-[#F5F5F5] shadow text-gray-700 resize-none focus:outline-none font-mulish overflow-hidden"
            placeholder={placeholder_text || "Type your answer here..."}
            rows={2}
            style={{ minHeight: '80px' }}
          />
        );

      case QuestionTypes.MULTIPLE_CHOICE:
        return (
          <div className="space-y-4">
            {options?.map((option, index) => (
              <label
                key={index}
                className="block w-full cursor-pointer"
              >
                <div className={`min-h-[46px] max-w-[510px] mx-auto ${
                  response === option
                    ? 'bg-gradient-to-b from-[#007077] to-[#05574A] text-white'
                    : 'bg-[#F1F3F6] text-gray-700 hover:bg-gradient-to-b hover:from-[#007077] hover:to-[#05574A] hover:text-white'
                } border border-[#F5F5F5] shadow rounded-lg transition-all duration-200 p-2`}>
                  <input
                    {...commonProps}
                    type="radio"
                    name="multiple_choice"
                    value={option}
                    checked={response === option}
                    onChange={(e) => handleResponseChange(e.target.value)}
                    className="sr-only"
                  />
                  <p className="font-mulish font-bold text-lg py-1 px-1 text-left">{option}</p>
                </div>
              </label>
            ))}
          </div>
        );

      case QuestionTypes.CHECKBOX:
        return (
          <div className="space-y-4">
            {options?.map((option, index) => (
              <label
                key={index}
                className="block w-full cursor-pointer"
              >
                <div className={`min-h-[46px] max-w-[510px] mx-auto ${
                  response?.includes(option)
                    ? 'bg-gradient-to-b from-[#007077] to-[#05574A] text-white'
                    : 'bg-[#F1F3F6] text-gray-700 hover:bg-gradient-to-b hover:from-[#007077] hover:to-[#05574A] hover:text-white'
                } border border-[#F5F5F5] shadow rounded-lg transition-all duration-200 p-2`}>
                  <input
                    {...commonProps}
                    type="checkbox"
                    value={option}
                    checked={response?.includes(option)}
                    onChange={(e) => {
                      const newResponse = response || [];
                      if (e.target.checked) {
                        handleResponseChange([...newResponse, option]);
                      } else {
                        handleResponseChange(newResponse.filter(item => item !== option));
                      }
                    }}
                    className="sr-only"
                  />
                  <p className="font-mulish font-bold text-lg py-1 px-1 text-left">{option}</p>
                </div>
              </label>
            ))}
          </div>
        );

      case QuestionTypes.YES_NO:
        return (
          <div className="space-y-4">
            {['Yes', 'No'].map((option) => (
              <label
                key={option}
                className="block w-full cursor-pointer"
              >
                <div className={`min-h-[46px] max-w-[510px] mx-auto ${
                  response === option.toLowerCase()
                    ? 'bg-gradient-to-b from-[#007077] to-[#05574A] text-white'
                    : 'bg-[#F1F3F6] text-gray-700 hover:bg-gradient-to-b hover:from-[#007077] hover:to-[#05574A] hover:text-white'
                } border border-[#F5F5F5] shadow rounded-lg transition-all duration-200 p-2`}>
                  <input
                    {...commonProps}
                    type="radio"
                    name="yes_no"
                    value={option.toLowerCase()}
                    checked={response === option.toLowerCase()}
                    onChange={(e) => handleResponseChange(e.target.value)}
                    className="sr-only"
                  />
                  <p className="font-mulish font-bold text-lg py-1 px-1 text-left">{option}</p>
                </div>
              </label>
            ))}
          </div>
        );

      case QuestionTypes.RATING:
        const ratingValue = response !== null ? response : null;
        const minVal = options?.min_value || min_value || 1;
        const maxVal = options?.max_value || max_value || 10;
        const step = options?.step || step_value || 1;
        
        return (
          <div className="space-y-4 max-w-[510px] mx-auto">
            <input
              {...commonProps}
              type="range"
              min={minVal}
              max={maxVal}
              step={step}
              value={ratingValue}
              onChange={(e) => handleResponseChange(parseFloat(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-600">
              <span>{minVal}</span>
              <span className="text-xl font-semibold text-[#007077]">
                {ratingValue}
              </span>
              <span>{maxVal}</span>
            </div>
          </div>
        );

      case QuestionTypes.LIKERT:
        // Generate Likert scale options based on min_value, max_value and step
        let likertOptions = [];
        
        if (options?.scale) {
          // Handle the object format (new API response structure)
          const { min_value = 1, max_value = 5, step = 1, labels = [] } = options.scale;
          
          // Generate options from min to max value
          for (let i = min_value; i <= max_value; i += step) {
            likertOptions.push({
              value: i,
              label: labels[i - min_value] || `${i}`
            });
          }
        } else if (Array.isArray(options)) {
          // Handle the old array format for backward compatibility
          likertOptions = options.map(opt => 
            typeof opt === 'object' ? opt : { value: opt, label: `${opt}` }
          );
        }
        
        if (!likertOptions.length) {
          return (
            <div className="text-red-500 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              No options available for this question
            </div>
          );
        }
        
        return (
          <div className="space-y-4">
            {likertOptions.map((option, index) => (
              <label
                key={index}
                className="block w-full cursor-pointer"
              >
                <div className={`min-h-[46px] max-w-[510px] mx-auto ${
                  response === String(option.value)
                    ? 'bg-gradient-to-b from-[#007077] to-[#05574A] text-white'
                    : 'bg-[#F1F3F6] text-gray-700 hover:bg-gradient-to-b hover:from-[#007077] hover:to-[#05574A] hover:text-white'
                } border border-[#F5F5F5] shadow rounded-lg transition-all duration-200 p-2`}>
                  <input
                    {...commonProps}
                    type="radio"
                    name="likert"
                    value={String(option.value)}
                    checked={response === String(option.value)}
                    onChange={(e) => handleResponseChange(String(e.target.value))}
                    className="sr-only"
                  />
                  <p className="font-mulish font-bold text-lg py-1 px-1 text-left">{option.label}</p>
                </div>
              </label>
            ))}
          </div>
        );

      case QuestionTypes.DROPDOWN:
        if (!options?.length) {
          return (
            <div className="text-red-500 flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              No options available for this dropdown
            </div>
          );
        }
        
        return (
          <div className="max-w-[510px] mx-auto">
            <select
              {...commonProps}
              value={response || ''}
              onChange={(e) => handleResponseChange(e.target.value)}
              className="w-full p-4 bg-[#F1F3F6] rounded-lg border border-[#F5F5F5] shadow text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#007077] cursor-pointer font-mulish font-bold text-lg"
            >
              <option value="">Select an option...</option>
              {options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        );

      case QuestionTypes.NUMERIC:
        return (
          <input
            {...commonProps}
            type="number"
            value={response === null ? '' : response}
            onChange={(e) => handleResponseChange(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full p-4 bg-[#F1F3F6] rounded-lg border border-[#F5F5F5] shadow text-gray-700 focus:outline-none font-mulish"
            placeholder={placeholder_text || "Enter a number..."}
            step="any"
          />
        );
        
      case QuestionTypes.DATE:
        const minDate = options?.min_date || '';
        const maxDate = options?.max_date || '';

        return (
          <input
            {...commonProps}
            type="date"
            min={minDate}
            max={maxDate}
            value={response === null ? '' : response}
            onChange={(e) => handleResponseChange(e.target.value)}
            className="w-full p-4 bg-[#F1F3F6] rounded-lg border border-[#F5F5F5] shadow text-gray-700 focus:outline-none font-mulish"
          />
        );

      case QuestionTypes.EMAIL:
        return (
          <input
            {...commonProps}
            type="email"
            value={response === null ? '' : response}
            onChange={(e) => handleResponseChange(e.target.value)}
            className="w-full p-4 bg-[#F1F3F6] rounded-lg border border-[#F5F5F5] shadow text-gray-700 focus:outline-none font-mulish"
            placeholder={placeholder_text || "Enter your email address..."}
            autoComplete="email"
          />
        );

      case QuestionTypes.NOMINAL:
        // Nominal: categorical choice without ordinal semantics (similar to multiple_choice)
        // Options may be array of strings or array of {label, value} objects
        const nominalOptions = Array.isArray(options) ? options : [];
        return (
          <div className="space-y-4">
            {nominalOptions.map((option, index) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              const optionValueStr = String(optionValue);
              
              return (
                <label
                  key={index}
                  className="block w-full cursor-pointer"
                >
                  <div className={`min-h-[46px] max-w-[510px] mx-auto ${
                    String(response) === optionValueStr
                      ? 'bg-gradient-to-b from-[#007077] to-[#05574A] text-white'
                      : 'bg-[#F1F3F6] text-gray-700 hover:bg-gradient-to-b hover:from-[#007077] hover:to-[#05574A] hover:text-white'
                  } border border-[#F5F5F5] shadow rounded-lg transition-all duration-200 p-2`}>
                    <input
                      {...commonProps}
                      type="radio"
                      name="nominal"
                      value={optionValueStr}
                      checked={String(response) === optionValueStr}
                      onChange={(e) => handleResponseChange(e.target.value)}
                      className="sr-only"
                    />
                    <p className="font-mulish font-bold text-lg py-1 px-1 text-left">{optionLabel}</p>
                  </div>
                </label>
              );
            })}
          </div>
        );

      case QuestionTypes.BOOLEAN:
        // Boolean: True/False binary choice
        // Options may be array of {label, value} objects or default to Yes/No
        const booleanOptions = Array.isArray(options) && options.length > 0
          ? options
          : [{ label: 'Yes', value: 0 }, { label: 'No', value: 1 }];
        
        return (
          <div className="space-y-4">
            {booleanOptions.map((option, index) => {
              const optionValue = typeof option === 'object' ? option.value : option;
              const optionLabel = typeof option === 'object' ? option.label : option;
              const optionValueStr = String(optionValue);
              
              return (
                <label
                  key={index}
                  className="block w-full cursor-pointer"
                >
                  <div className={`min-h-[46px] max-w-[510px] mx-auto ${
                    String(response) === optionValueStr
                      ? 'bg-gradient-to-b from-[#007077] to-[#05574A] text-white'
                      : 'bg-[#F1F3F6] text-gray-700 hover:bg-gradient-to-b hover:from-[#007077] hover:to-[#05574A] hover:text-white'
                  } border border-[#F5F5F5] shadow rounded-lg transition-all duration-200 p-2`}>
                    <input
                      {...commonProps}
                      type="radio"
                      name="boolean"
                      value={optionValueStr}
                      checked={String(response) === optionValueStr}
                      onChange={(e) => handleResponseChange(e.target.value)}
                      className="sr-only"
                    />
                    <p className="font-mulish font-bold text-lg py-1 px-1 text-left">{optionLabel}</p>
                  </div>
                </label>
              );
            })}
          </div>
        );

      default:
        return (
          <div className="flex items-center text-red-500">
            <AlertCircle className="w-5 h-5 mr-2" />
            Unsupported question type: {type}
          </div>
        );
    }
  };

  const checkForLinkedSurveys = async () => {
    if (!assignmentId || isCheckingLinkedSurveys) return;
    
    setIsCheckingLinkedSurveys(true);
    try {
      console.log("Checking for next linked survey assignment...");
      const nextLinkedAssignment = await svc.getNextLinkedAssignment(assignmentId);
      console.log("Next linked assignment response:", nextLinkedAssignment);
      
      if (nextLinkedAssignment && nextLinkedAssignment.id) {
        console.log("Found next linked survey, navigating to:", nextLinkedAssignment.id);
        // Navigate to the next linked survey instead of showing completion screen
        // Force a full page reload to ensure the component remounts
        window.location.href = `/survey-taking/${nextLinkedAssignment.id}`;
      }
    } catch (err) {
      console.error("Error checking for linked surveys:", err);
      // Keep the success state true to show completion screen on error
    } finally {
      setIsCheckingLinkedSurveys(false);
    }
  };

  // ASSESSMENT BUILDER: Handle screen response changes
  const handleScreenResponseChange = (questionId, value) => {
    setScreenResponses(prev => {
      const next = { ...prev, [questionId]: value };
      // Persist to session map immediately so answers survive re-mounts and nav in any direction
      if (currentScreen?.screen_id != null) {
        responseMapRef.current[currentScreen.screen_id] = { ...next };
      }
      return next;
    });

    // Clear validation error for this question
    if (screenValidationErrors[questionId]) {
      setScreenValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  // ASSESSMENT BUILDER: Handle screen navigation (fast-nav: instant local, async saves)
  // actionType: 'back' | 'next' | 'jump' | 'submit' | 'skip'
  const handleScreenNavigation = async (actionType, responses, element = null) => {
    const currentResponses = responses || screenResponses;

    // Always persist current screen's answers to the session map so re-visits preserve them
    if (currentScreen?.screen_id != null) {
      responseMapRef.current[currentScreen.screen_id] = { ...currentResponses };
    }

    // ── BACK ──────────────────────────────────────────────────────────────────
    if (actionType === 'back') {
      if (assessmentSettings.allow_back_navigation === false) return;
      if (screenHistory.length > 0) {
        const prevHistoryEntry = screenHistory[screenHistory.length - 1];
        const prevIndex = allScreens.findIndex(s => s.screen_id === prevHistoryEntry.screen_id);
        const prevScreen = prevIndex >= 0 ? allScreens[prevIndex] : prevHistoryEntry;
        setScreenHistory(prev => prev.slice(0, -1));
        setCurrentScreen(prevScreen);
        setCurrentScreenIndex(prevIndex >= 0 ? prevIndex : currentScreenIndex - 1);
        setScreenResponses(responseMapRef.current[prevScreen.screen_id] || prevHistoryEntry.existing_responses || {});
        setScreenValidationErrors({});
        setIsFirstQuestion(prevIndex <= 0);
        setIsLastQuestion(prevIndex >= allScreens.length - 1);
        screenStartTimeRef.current = Date.now();
      }
      return;
    }

    // ── VALIDATION ────────────────────────────────────────────────────────────
    // NOTE: per-screen required-question validation was removed intentionally.
    // Skip-logic rules are now authoritative — if a rule fires, we honour it
    // even when the current screen has unanswered required questions. The
    // "required must be answered" check moves to submit time (see
    // `validateRequiredAtSubmit` below), and a required question is only
    // flagged as missing if it was VISITED but not answered AND not skipped
    // by a rule. Questions bypassed by a skip rule are recorded in
    // `skippedQuestions` and transmitted to the backend, which applies the
    // same rule.
    setScreenValidationErrors({});

    // ── BUILD responsesToSave ─────────────────────────────────────────────────
    const responsesToSave = { ...currentResponses };
    if ((actionType === 'submit' || isLastQuestion) && currentScreen?.elements) {
      for (const el of currentScreen.elements) {
        if (el.type !== 'question') continue;
        if (!(String(el.questionId) in responsesToSave)) {
          responsesToSave[String(el.questionId)] = null;
        }
      }
    }

    // ── FIRE SAVES ASYNC (non-blocking) ───────────────────────────────────────
    // Attach timing metadata so the backend has per-screen engagement data without blocking the participant.
    const nowMs = Date.now();
    const timeOnScreenMs = screenStartTimeRef.current ? nowMs - screenStartTimeRef.current : null;
    const answeredAt = new Date(nowMs).toISOString();
    const screenIdForPayload = currentScreen?.screen_id ?? null;

    const saveEntries = Object.entries(responsesToSave);
    if (isAdaptiveAssessment) {
      const answeredEntries = saveEntries.filter(([, answer]) =>
        answer !== null && answer !== undefined && answer !== '' &&
        !(Array.isArray(answer) && answer.length === 0)
      );

      if (answeredEntries.length === 0) {
        const firstQuestion = currentScreen?.elements?.find(el => el.type === 'question');
        if (firstQuestion?.questionId) {
          setScreenValidationErrors({ [firstQuestion.questionId]: 'This question is required' });
        }
        return;
      }

      setSaving(true);
      try {
        let nextStatusList = statusList;
        let lastSaveResponse = null;

        for (const [questionId, answer] of answeredEntries) {
          const payload = {
            answer,
            time_on_screen_ms: timeOnScreenMs,
            answered_at: answeredAt,
            screen_id: screenIdForPayload,
            action: actionType
          };
          lastSaveResponse = await svc.saveResponse(
            assignmentId,
            parseInt(questionId),
            payload,
            nextStatusList
          );
          nextStatusList = lastSaveResponse.status_list;
          setStatusList(nextStatusList);
        }

        if (lastSaveResponse?.is_last_question) {
          setIsCheckingLinkedSurveys(true);
          try {
            const nextLinkedAssignment = await svc.getNextLinkedAssignment(assignmentId);
            if (nextLinkedAssignment?.id) {
              window.location.href = `/survey-taking/${nextLinkedAssignment.id}`;
              return;
            }
          } catch (err) {
            console.error("Error checking for linked surveys:", err);
          } finally {
            setIsCheckingLinkedSurveys(false);
          }
          setCurrentScreen(null);
          setSuccess(true);
          return;
        }

        await fetchNextQuestion(nextStatusList);
      } catch (err) {
        handleError(err, ERROR_CODES.SURVEY.SAVE_FAILED);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (saveEntries.length > 0) {
      const savePromise = Promise.all(
        saveEntries.map(([questionId, answer]) => {
          // Metadata only attaches to real answers — the backend treats an empty response_data
          // as "no answer" for optional questions, and any non-empty dict triggers type-specific
          // validation that would fail on a missing `answer` key.
          const payload = answer !== null
            ? {
                answer,
                time_on_screen_ms: timeOnScreenMs,
                answered_at: answeredAt,
                screen_id: screenIdForPayload,
                action: actionType
              }
            : {};
          return svc.saveResponse(assignmentId, parseInt(questionId), payload, statusList);
        })
      );
      pendingSavePromises.current.push(savePromise);
    }

    // ── CLIENT-SIDE NAV RULE EVALUATION ──────────────────────────────────────
    const navResult = evaluateNavRulesClientSide(currentResponses, currentScreenIndex);

    // ── SUBMIT / END SURVEY ───────────────────────────────────────────────────
    const isSubmit = actionType === 'submit' || navResult.action === 'end_survey' ||
      (actionType !== 'jump' && currentScreenIndex >= allScreens.length - 1);

    if (isSubmit) {
      // end_survey fired by a rule bypasses every later screen — record them.
      let finalSkipped = skippedQuestions;
      if (navResult.action === 'end_survey') {
        const endBypass = buildSkipRecordsForBypass(
          currentScreenIndex,
          allScreens.length,
          allScreens,
          navResult.rule_id,
        );
        if (endBypass.length > 0) {
          finalSkipped = mergeSkipRecords(finalSkipped, endBypass);
          setSkippedQuestions(finalSkipped);
        }
      }

      // Skip-aware submit-time required validation. A required question is
      // only "missing" if visited AND not skipped-by-rule AND unanswered.
      // Flatten responseMapRef (keyed by screen_id) into a single question
      // map that mirrors how the backend will see responses; overlay the
      // current screen's in-flight responses on top.
      const flatResponses = {};
      Object.values(responseMapRef.current).forEach(screenResp => {
        Object.assign(flatResponses, screenResp);
      });
      Object.assign(flatResponses, currentResponses);

      const skippedIds = skippedQuestionIdSet(finalSkipped);
      const missing = validateRequiredAtSubmit(
        allScreens,
        flatResponses,
        visitedQuestionIds,
        skippedIds,
      );

      if (missing.length > 0) {
        // Jump back to the first screen containing a missing required question
        // and surface the validation errors to the participant.
        const firstMissing = missing[0];
        const targetIdx = allScreens.findIndex(s =>
          (s.elements || []).some(el => el.type === 'question' && String(el.questionId) === firstMissing)
        );
        const errors = {};
        for (const qid of missing) errors[qid] = 'This question is required';
        if (targetIdx >= 0 && targetIdx !== currentScreenIndex) {
          setScreenHistory(prev => [...prev, { ...currentScreen, existing_responses: currentResponses }]);
          navigateToIndex(targetIdx, currentResponses);
        }
        setScreenValidationErrors(errors);
        return;
      }

      try {
        setIsCheckingLinkedSurveys(true);
        await flushPendingSaves();
        await svc.submitSurvey(assignmentId, { skipped_questions: finalSkipped });
        await svc.updateAssignmentStatus(assignmentId, 'completed');
        const nextLinkedAssignment = await svc.getNextLinkedAssignment(assignmentId);
        if (nextLinkedAssignment?.id) {
          window.location.href = `/survey-taking/${nextLinkedAssignment.id}`;
          return;
        }
      } catch (err) {
        console.error('Error completing survey:', err);
      } finally {
        setIsCheckingLinkedSurveys(false);
      }
      setCurrentScreen(null);
      setSuccess(true);
      return;
    }

    // ── SHOW ALERT ────────────────────────────────────────────────────────────
    if (navResult.action === 'show_alert' && navResult.alert_message) {
      setNavigationAlert({
        message: navResult.alert_message,
        onContinue: () => {
          setNavigationAlert(null);
          navigateToIndex(currentScreenIndex + 1, currentResponses);
        }
      });
      return;
    }

    // ── JUMP to specific screen by ID ─────────────────────────────────────────
    // Manual jumps (custom canvas buttons) don't carry a rule id; use null.
    if (actionType === 'jump' && element?.targetScreen) {
      const targetIdx = allScreens.findIndex(s => String(s.screen_id) === String(element.targetScreen));
      if (targetIdx >= 0) {
        const bypassRecords = buildSkipRecordsForBypass(
          currentScreenIndex, targetIdx, allScreens, element?.id ? `button:${element.id}` : null
        );
        if (bypassRecords.length > 0) {
          setSkippedQuestions(prev => mergeSkipRecords(prev, bypassRecords));
        }
        setScreenHistory(prev => [...prev, { ...currentScreen, existing_responses: currentResponses }]);
        navigateToIndex(targetIdx, currentResponses);
      }
      return;
    }

    // ── SKIP_TO nav rule ──────────────────────────────────────────────────────
    if (navResult.action === 'skip_to') {
      // Resolve target in priority order: id → name → rank. Stale target_screen_id
      // is common after clone/republish (screens get new IDs); name and rank are
      // carried on the rule for exactly this fallback path.
      let targetIdx = -1;
      if (navResult.next_screen_id) {
        targetIdx = allScreens.findIndex(s => String(s.screen_id) === String(navResult.next_screen_id));
      }
      if (targetIdx < 0 && navResult.target_screen_name) {
        targetIdx = allScreens.findIndex(s => s.screen_name === navResult.target_screen_name);
      }
      if (targetIdx < 0 && Number.isInteger(navResult.target_screen_index)) {
        const byRank = allScreens.findIndex(s => s.screen_rank === navResult.target_screen_index);
        if (byRank >= 0) targetIdx = byRank;
        else if (navResult.target_screen_index >= 0 && navResult.target_screen_index < allScreens.length) {
          targetIdx = navResult.target_screen_index;
        }
      }
      if (targetIdx >= 0) {
        const bypassRecords = buildSkipRecordsForBypass(
          currentScreenIndex, targetIdx, allScreens, navResult.rule_id
        );
        if (bypassRecords.length > 0) {
          setSkippedQuestions(prev => mergeSkipRecords(prev, bypassRecords));
        }
        setScreenHistory(prev => [...prev, { ...currentScreen, existing_responses: currentResponses }]);
        navigateToIndex(targetIdx, currentResponses);
        return;
      }
      console.warn('[navRules] skip_to target unresolved — falling through to sequential next', {
        rule_id: navResult.rule_id,
        next_screen_id: navResult.next_screen_id,
        target_screen_name: navResult.target_screen_name,
        target_screen_index: navResult.target_screen_index,
        available_screens: allScreens.map(s => ({ screen_id: s.screen_id, screen_name: s.screen_name, screen_rank: s.screen_rank })),
      });
    }

    // ── NEXT (default) ────────────────────────────────────────────────────────
    setScreenHistory(prev => [...prev, { ...currentScreen, existing_responses: currentResponses }]);
    navigateToIndex(currentScreenIndex + 1, currentResponses);
  };

  // Instantly switch to a screen by index (zero network latency)
  const navigateToIndex = (nextIndex, currentResponses) => {
    if (nextIndex < 0 || nextIndex >= allScreens.length) return;
    const nextScreen = allScreens[nextIndex];
    setCurrentScreen(nextScreen);
    setCurrentScreenIndex(nextIndex);
    setScreenResponses(responseMapRef.current[nextScreen.screen_id] || nextScreen.existing_responses || {});
    setScreenValidationErrors({});
    setIsFirstQuestion(nextIndex === 0);
    setIsLastQuestion(nextIndex >= allScreens.length - 1);
    setAnsweredQuestions(nextIndex);
    setProgress(allScreens.length > 0 ? Math.round((nextIndex / allScreens.length) * 100) : 0);
    screenStartTimeRef.current = Date.now();
  };
  
  // Check if all REQUIRED questions on the current screen have been answered
  const hasScreenResponse = () => {
    if (!currentScreen || !currentScreen.elements) return false;
    const questionElements = currentScreen.elements.filter(el => el.type === 'question');
    if (questionElements.length === 0) return true;  // No questions means can proceed

    // Source of truth for required is currentScreen.navigationRules[questionId].is_required
    const navRules = currentScreen.navigationRules || {};
    const isElementRequired = (el) => navRules[String(el.questionId)]?.is_required === true;

    // Check if all REQUIRED questions have responses
    const requiredQuestions = questionElements.filter(isElementRequired);

    console.log('[hasScreenResponse] Debug:', {
      totalQuestions: questionElements.length,
      requiredQuestions: requiredQuestions.length,
      questionDetails: questionElements.map(el => ({
        id: el.id,
        questionId: el.questionId,
        required: isElementRequired(el),
        hasResponse: screenResponses[el.questionId] !== undefined
      })),
      screenResponses
    });
    
    if (requiredQuestions.length === 0) {
      // No required questions - check if ANY question is answered (original behavior)
      return questionElements.some(el => 
        screenResponses[el.questionId] !== undefined && 
        screenResponses[el.questionId] !== null && 
        screenResponses[el.questionId] !== ''
      );
    }
    
    // All required questions must be answered
    return requiredQuestions.every(el => {
      const response = screenResponses[el.questionId];
      return response !== undefined && 
             response !== null && 
             response !== '' &&
             !(Array.isArray(response) && response.length === 0);
    });
  };

  // Check if current screen has custom navigation buttons from Assessment Builder
  const hasCustomButtons = () => {
    if (!currentScreen || !currentScreen.elements) return false;
    return currentScreen.elements.some(el => el.type === 'button');
  };

  const progressClassNames = statusList == null || statusList.length < 10 ? "mb-8" : "hidden"
  if (loading || isCheckingLinkedSurveys) {
    if (isAssessmentMode) {
      return (
        <div className="fixed inset-0 bg-gray-100 z-[100] flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-t-transparent border-[#007077] rounded-full animate-spin"></div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-6 h-6 border-2 border-t-transparent border-[#007077] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (success && !currentQuestion) {
    return (
      <div className="flex w-full justify-center px-4 py-8 sm:py-12">
        <div
          className="survey-complete-card flex w-full max-w-xl flex-col items-center rounded-3xl bg-white px-8 py-12 text-center ring-1 ring-black/[0.06] animate-fade-in"
          style={{
            boxShadow:
              '0 1px 2px rgba(16,24,40,0.04), 0 24px 48px -24px rgba(16,24,40,0.22), 0 8px 20px -14px rgba(16,24,40,0.12)'
          }}
        >
          <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-50 ring-1 ring-primary-100">
            <CheckCircle className="h-8 w-8 text-primary-600" strokeWidth={2.25} />
          </div>
          <h2 className="text-2xl font-semibold tracking-[-0.01em] text-gray-900">Survey complete</h2>
          <p className="mt-3 max-w-md text-[15px] leading-relaxed text-gray-500">
            Thank you for taking the time to complete this survey. Your responses have been recorded.
          </p>
          <button
            onClick={() => navigate(homePath)}
            className="mt-8 inline-flex items-center justify-center rounded-xl bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
          >
            Return to dashboard
          </button>
        </div>
      </div>
    );
  }

  // ASSESSMENT MODE: Completely separate rendering without legacy CSS
  if (isAssessmentMode && currentScreen) {
    const screenLayout = currentScreen.layout || {};
    const screenWidth = screenLayout.width || 1200;

    // FECOA-1468 — premium participant shell. The canvas type drives the frame:
    // a responsive canvas fills the full available width (Design A); any fixed
    // device canvas is centred in a device-width card (Design C). Everything
    // OUTSIDE the canvas (the frame + chrome) is ours to perfect; everything
    // inside stays exactly as the builder designed it. This is presentation
    // only — the live canvas, navigation, validation and saves are untouched.
    const canvasDeviceMode = screenLayout.deviceMode;
    const isResponsiveCanvas = canvasDeviceMode === 'responsive';
    const canvasDeviceCategory = DEVICE_DIMENSIONS[canvasDeviceMode]?.category || 'desktop';
    const cardMaxWidthPx = isResponsiveCanvas
      ? null
      : canvasDeviceCategory === 'phone'
        ? 480
        : canvasDeviceCategory === 'tablet'
          ? 900
          : 1200;
    const showShellProgress = assessmentSettings.show_progress_bar !== false;
    const shellProgress = Math.max(0, Math.min(Math.round(progress) || 0, 100));
    const totalShellSteps = allScreens.length;
    const currentShellStep = totalShellSteps ? Math.min(currentScreenIndex + 1, totalShellSteps) : null;

    // Responsive canvases (Design A) break out of the centred <main> and fill the
    // entire viewport beneath the app banner — no card. The top offset mirrors the
    // banner clearance Layout applies (localDevLayout), so the focus shell sits
    // flush under the flexcoa banner on every breakpoint. The banner itself stays
    // on top (z-50); the focus shell sits just below it (z-30).
    const responsiveTopOffsetClass = isLocalDevLayoutActive()
      ? 'top-24 md:top-28'
      : 'top-16 md:top-20';

    // Title + live progress chrome shared by both shells. Rendered OUTSIDE the
    // scaled canvas so it stays crisp and never shrinks with the device frame.
    const shellChromeInner = (
      <>
        {/* Assessment identity — a haloed status dot reads as "live / in progress". */}
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500 shadow-[0_0_0_3px_rgba(0,119,100,0.12)]"
            aria-hidden="true"
          />
          <h1
            className="truncate text-[15px] font-semibold tracking-[-0.015em] text-gray-900"
            title={surveyName || 'Survey'}
          >
            {surveyName || 'Survey'}
          </h1>
        </div>

        {/* Autosave state — honest, app-controlled, and absolutely centred so it
             never nudges the title or progress when it appears mid-navigation. */}
        {saving && (
          <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 sm:block">
            <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-500 ring-1 ring-gray-200/80">
              <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500" aria-hidden="true" />
              Saving
            </span>
          </div>
        )}

        {/* Live progress — a crisp completion ring + percentage, with step context. */}
        {showShellProgress && (
          <div className="flex flex-shrink-0 items-center gap-3">
            {currentShellStep && (
              <span className="hidden text-xs font-medium tabular-nums text-gray-400 sm:inline">
                Step {currentShellStep} of {totalShellSteps}
              </span>
            )}
            <div className="flex items-center gap-2">
              <ProgressRing value={shellProgress} />
              <span className="text-sm font-semibold tabular-nums text-gray-700">{shellProgress}%</span>
            </div>
          </div>
        )}
      </>
    );

    // Top-edge progress hairline — premium page-load feel, app-controlled and
    // always crisp (never scaled with the canvas).
    const shellProgressHairline = showShellProgress ? (
      <div className="absolute inset-x-0 top-0 z-20 h-[3px] overflow-hidden bg-gray-100" aria-hidden="true">
        <div
          className="h-full rounded-r-full bg-primary-600 shadow-[0_0_10px_rgba(0,119,100,0.45)] transition-[width] duration-700 ease-out"
          style={{ width: `${shellProgress}%` }}
        />
      </div>
    ) : null;

    // Quiet reassurance footer (Design C). Pure chrome — communicates trust without
    // competing with the content. Kept off the responsive focus shell to preserve
    // maximum canvas height.
    const shellFooter = (
      <div className="flex flex-shrink-0 items-center justify-center gap-1.5 border-t border-gray-100 px-5 py-2.5 text-[11px] font-medium tracking-[0.01em] text-gray-400">
        <ShieldCheck className="h-3.5 w-3.5 text-gray-300" aria-hidden="true" />
        <span>Your responses are recorded securely</span>
      </div>
    );

    // Live canvas props are identical for both shells — only the viewport framing
    // differs (a responsive canvas contain-fits to fill; a fixed canvas centres in
    // its device-width card). The canvas itself, navigation, validation and saves
    // are untouched: this is presentation only.
    const screenRendererProps = {
      screenData: currentScreen,
      responses: screenResponses,
      onResponseChange: handleScreenResponseChange,
      onNavigate: handleScreenNavigation,
      validationErrors: screenValidationErrors,
      disabled: saving,
      isFirstQuestion,
      allowBackNavigation: !isAdaptiveAssessment && assessmentSettings.allow_back_navigation !== false,
      canvasClassName: 'bg-transparent overflow-hidden',
    };

    return (
      <>
        <ErrorPopup
          error={errorObject}
          onClose={clearError}
          onRetry={handleRetry}
          showRetry={true}
          showContact={true}
        />
        
        {/* Navigation Alert Modal - for skip logic alerts */}
        {navigationAlert && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-amber-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Important Notice</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-700">{navigationAlert.message}</p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={navigationAlert.onContinue}
                  className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        )}
        
        {isResponsiveCanvas ? (
          /* Design A — full-bleed focus. A responsive canvas behaves like a real
             browser window: it breaks out of the centred <main> and fills the whole
             viewport beneath the app banner (no card), while ScreenRenderer
             contain-fits the design so every element scales together and stays
             centred. The banner (z-50) stays on top; this focus shell sits just
             below it (z-30). Functionally identical to the card shell. */
          <div
            className={`assessment-taking-container survey-taking-page survey-taking-responsive fixed inset-x-0 bottom-0 ${responsiveTopOffsetClass} z-30 flex max-w-none flex-col bg-white`}
          >
            {shellProgressHairline}

            {/* Borderless chrome — survey identity + progress, kept OUTSIDE the
                 scaled canvas so it never blurs or shrinks with the design. */}
            <div className="survey-taking-chrome relative flex h-16 flex-shrink-0 items-center justify-between gap-3 px-5 sm:px-8">
              {shellChromeInner}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={canvasFadeControls}
              style={{ willChange: 'opacity' }}
              className="flex min-h-0 w-full flex-1 flex-col"
            >
              <ScreenRenderer
                {...screenRendererProps}
                viewportClassName="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden bg-transparent"
              />
            </motion.div>
          </div>
        ) : (
          /* Design C — device-width card. Any fixed device canvas is centred in a
             bounded, premium card. */
          <div className="assessment-taking-container survey-taking-page mx-auto flex w-full max-w-6xl justify-center px-1 py-2 sm:px-3 sm:py-4">
            <div
              className="survey-taking-card relative flex w-full flex-col overflow-hidden rounded-[28px] bg-white ring-1 ring-black/[0.06]"
              style={{
                maxWidth: cardMaxWidthPx ? `${cardMaxWidthPx}px` : undefined,
                boxShadow:
                  '0 1px 2px rgba(16,24,40,0.05), 0 2px 6px -2px rgba(16,24,40,0.08), 0 28px 56px -28px rgba(16,24,40,0.24), 0 12px 28px -18px rgba(16,24,40,0.14)'
              }}
            >
              {shellProgressHairline}

              {/* Chrome header — survey identity + progress, kept OUTSIDE the scaled
                   canvas so it never blurs or shrinks with the device frame. */}
              <div className="survey-taking-chrome relative flex h-16 flex-shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-5 sm:px-6">
                {shellChromeInner}
              </div>

              {/* Live canvas — delegated to ScreenRenderer so participants see
                   exactly what the builder designed (and what Preview shows). */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={canvasFadeControls}
                style={{ willChange: 'opacity' }}
                className="flex w-full flex-col"
              >
                <ScreenRenderer
                  {...screenRendererProps}
                  viewportClassName="flex min-h-[700px] w-full items-center justify-center bg-transparent p-8"
                />
              </motion.div>

              {shellFooter}
            </div>
          </div>
        )}

        {/* Legacy navigation footer — retained as dead code; custom canvas buttons
             drive navigation. Never rendered. */}
        {false && !hasCustomButtons() && (
            <div className="flex justify-center pb-4 bg-gray-100">
              <div 
                className="flex items-center justify-between px-4 py-3"
                style={{ width: `${Math.min(screenWidth, window.innerWidth - 40)}px`, maxWidth: '100%' }}
              >
                {/* Back button - only show if allow_back_navigation is enabled */}
                {assessmentSettings.allow_back_navigation !== false ? (
                  <button
                    onClick={() => handleScreenNavigation('back', screenResponses)}
                    disabled={saving || isFirstQuestion}
                    className={`flex items-center px-5 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                      saving || isFirstQuestion 
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 shadow-sm'
                    }`}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </button>
                ) : (
                  <div /> /* Empty spacer when back nav disabled */
                )}

                {/* Progress indicator - only show if show_progress_bar is enabled */}
                {assessmentSettings.show_progress_bar !== false && (
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>{surveyName}</span>
                    <span>•</span>
                    <span>Screen {(currentScreen.screen_rank || 0) + 1}</span>
                    <div className="w-20 h-1.5 bg-gray-300 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary-600 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleScreenNavigation('next', screenResponses)}
                  disabled={saving || !hasScreenResponse()}
                  className={`flex items-center px-6 py-2.5 rounded-lg font-medium text-sm transition-all ${
                    saving || !hasScreenResponse()
                      ? 'bg-gray-200 text-gray-500 border border-gray-300 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm'
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2" />
                      Saving...
                    </>
                  ) : isLastQuestion ? (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Submit
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
      </>
    );
  }

  // LEGACY MODE: Original rendering with legacy CSS
  return (
    <>
      {/* Error Popup - shows as overlay when error exists */}
      <ErrorPopup
        error={errorObject}
        onClose={clearError}
        onRetry={handleRetry}
        showRetry={true}
        showContact={true}
      />
      
      {/* Legacy Survey Content */}
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-md border border-[#F5F5F5] p-8 question-modal">
          {surveyName && (
            <h1 className="text-[#05574A] font-mulish text-2xl font-bold text-center mb-6">{surveyName}</h1>
          )}

          <div className={progressClassNames}>
            <div className="h-1 bg-gray-200 rounded-full">
              <div 
                className="h-full bg-[#007077] rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>

          {/* LEGACY MODE: Render question-by-question */}
          {currentQuestion && (
            <div className="question_body_div">
              <div className="question_body_div_content">
                <div className="text-center mb-8">
                  <h2 className="font-mulish font-bold text-lg">
                  {currentQuestion.text}
                  </h2>
                </div>

                <div className="mb-8">
                  {renderQuestionInput()}
                </div>
              </div>

              <div className="question__row_footer_container">
                <button
                  onClick={fetchPreviousQuestion}
                  disabled={goingBack || isFirstQuestion}
                  className={`question-prev-button ${
                    goingBack || isFirstQuestion
                      ? 'cursor-not-allowed'
                      : ''
                  }`}
                >
                  {goingBack ? (
                    <>
                      <div className="w-4 h-4 border-2 border-t-transparent border-[#007077] rounded-full animate-spin mr-2"></div>
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <ArrowLeft className="w-4 h-4 mr-2 inline" />
                      <span>Previous</span>
                    </>
                  )}
                </button>

                <button
                  onClick={handleSaveResponse}
                  disabled={saving || !response}
                  className={`question-next-button ${
                    saving || !response
                      ? 'cursor-not-allowed'
                      : ''
                  } ${isLastQuestion ? 'is-last-question' : ''}`}
                  data-last-question={isLastQuestion}
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2 inline"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2 inline" />
                      {isLastQuestion ? (
                        <span className="submit-text">Submit</span>
                      ) : (
                        <span>Next</span>
                      )}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default SurveyTaking;